import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/shared/lib/utils/classnames';

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => {
  const checkboxRef = React.useRef<HTMLButtonElement>(null);
  const combinedRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      checkboxRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const handleFocus = React.useCallback((_: React.FocusEvent<HTMLButtonElement>) => {
    if (checkboxRef.current) {
      const isInvalid = checkboxRef.current.getAttribute('aria-invalid') === 'true';
      const computedStyle = window.getComputedStyle(checkboxRef.current);
      // border-radius를 명시적으로 유지
      const borderRadius = computedStyle.borderRadius || '2px';
      checkboxRef.current.style.setProperty('border-radius', borderRadius, 'important');

      // border-color만 변경
      if (!isInvalid) {
        checkboxRef.current.style.setProperty('border-color', '#000000', 'important');
      } else {
        checkboxRef.current.style.setProperty('border-color', '#ef4444', 'important');
      }

      // Ring/outline 제거
      checkboxRef.current.style.setProperty('outline', 'none', 'important');
      checkboxRef.current.style.setProperty('outline-width', '0', 'important');
      checkboxRef.current.style.setProperty('outline-style', 'none', 'important');
      checkboxRef.current.style.setProperty('outline-offset', '0', 'important');
      checkboxRef.current.style.setProperty('box-shadow', 'none', 'important');
      checkboxRef.current.style.setProperty('--tw-ring-offset-shadow', 'none', 'important');
      checkboxRef.current.style.setProperty('--tw-ring-shadow', 'none', 'important');
    }
  }, []);

  const handleBlur = React.useCallback((_: React.FocusEvent<HTMLButtonElement>) => {
    if (checkboxRef.current) {
      const isInvalid = checkboxRef.current.getAttribute('aria-invalid') === 'true';
      const computedStyle = window.getComputedStyle(checkboxRef.current);
      // border-radius를 명시적으로 유지
      const borderRadius = computedStyle.borderRadius || '2px';
      checkboxRef.current.style.setProperty('border-radius', borderRadius, 'important');

      if (isInvalid) {
        checkboxRef.current.style.setProperty('border-color', '#ef4444', 'important');
      } else {
        checkboxRef.current.style.removeProperty('border-color');
      }

      // Ring/outline 제거 유지
      checkboxRef.current.style.setProperty('outline', 'none', 'important');
      checkboxRef.current.style.setProperty('box-shadow', 'none', 'important');
    }
  }, []);

  return (
    <CheckboxPrimitive.Root
      ref={combinedRef}
      className={cn(
        'grid place-content-center peer h-4 w-4 shrink-0 rounded-sm border border-[#d9dde0]',
        // Ring/outline 완전히 제거
        'outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
        // Error 상태
        'aria-invalid:border-red-500',
        // Disabled 상태
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Checked 상태
        'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary',
        // Transition
        'transition-colors',
        className,
      )}
      style={{
        outline: 'none',
        boxShadow: 'none',
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    >
      <CheckboxPrimitive.Indicator className={cn('grid place-content-center text-current')}>
        <Check className="h-4 w-4" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
